import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatsCard from '../admin-dashboard/components/StatsCard';
import BillingAnalyticsPanel from './components/BillingAnalyticsPanel';
import PaymentHistoryTimeline from './components/PaymentHistoryTimeline';
import SuspensionManagementControls from './components/SuspensionManagementControls';
import TenantCommunicationLog from './components/TenantCommunicationLog';
import GracePeriodManager from './components/GracePeriodManager';

const TenantBillingSuspensionDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tenantIdFromUrl = searchParams?.get('tenant');
  const { user, isSuperAdmin, loading: authLoading } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [billingRecords, setBillingRecords] = useState([]);
  const [paymentAttempts, setPaymentAttempts] = useState([]);
  const [suspensionHistory, setSuspensionHistory] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({
    totalBilled: 0,
    totalPaid: 0,
    outstandingBalance: 0,
    paymentSuccessRate: 0,
    failedAttempts: 0,
    daysSinceLastPayment: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && (!user || !isSuperAdmin)) {
      navigate('/login');
    }
  }, [user, isSuperAdmin, authLoading, navigate]);

  useEffect(() => {
    if (isSuperAdmin && tenantIdFromUrl) {
      loadTenantBillingData(tenantIdFromUrl);
    }
  }, [isSuperAdmin, tenantIdFromUrl]);

  const loadTenantBillingData = async (tenantId) => {
    setLoading(true);
    setError('');

    try {
      // Load tenant details
      const { data: tenantData, error: tenantError } = await supabase
        ?.from('tenants')
        ?.select(`
          *,
          user_profiles:owner_id(id, full_name, email, phone)
        `)
        ?.eq('id', tenantId)
        ?.single();

      if (tenantError) throw tenantError;
      setTenant(tenantData);

      // Load billing status
      const { data: billingStatusData, error: billingStatusError } = await supabase
        ?.from('tenant_billing_status')
        ?.select('*')
        ?.eq('tenant_id', tenantId)
        ?.single();

      if (billingStatusError && billingStatusError?.code !== 'PGRST116') throw billingStatusError;
      setBillingStatus(billingStatusData);

      // Load billing records
      const { data: billingRecordsData, error: billingRecordsError } = await supabase
        ?.from('billing_records')
        ?.select('*')
        ?.eq('tenant_id', tenantId)
        ?.order('billing_date', { ascending: false })
        ?.limit(50);

      if (billingRecordsError) throw billingRecordsError;
      setBillingRecords(billingRecordsData || []);

      // Load payment attempts
      const { data: paymentAttemptsData, error: paymentAttemptsError } = await supabase
        ?.from('payment_attempts')
        ?.select('*')
        ?.eq('tenant_id', tenantId)
        ?.order('attempted_at', { ascending: false })
        ?.limit(50);

      if (paymentAttemptsError) throw paymentAttemptsError;
      setPaymentAttempts(paymentAttemptsData || []);

      // Load suspension history
      const { data: suspensionHistoryData, error: suspensionHistoryError } = await supabase
        ?.from('tenant_suspensions')
        ?.select(`
          *,
          suspended_by_profile:suspended_by(full_name),
          reactivated_by_profile:reactivated_by(full_name)
        `)
        ?.eq('tenant_id', tenantId)
        ?.order('suspended_at', { ascending: false });

      if (suspensionHistoryError) throw suspensionHistoryError;
      setSuspensionHistory(suspensionHistoryData || []);

      // Load notifications for this tenant
      const { data: notificationsData, error: notificationsError } = await supabase
        ?.from('notifications')
        ?.select('*')
        ?.eq('tenant_id', tenantId)
        ?.eq('type', 'system')
        ?.order('created_at', { ascending: false })
        ?.limit(20);

      if (notificationsError) throw notificationsError;
      setNotifications(notificationsData || []);

      // Calculate stats
      const totalBilled = billingRecordsData?.reduce((sum, b) => sum + parseFloat(b?.total_amount || 0), 0);
      const totalPaid = billingRecordsData
        ?.filter(b => b?.payment_status === 'completed')
        ?.reduce((sum, b) => sum + parseFloat(b?.total_amount || 0), 0);
      const completedPayments = billingRecordsData?.filter(b => b?.payment_status === 'completed')?.length || 0;
      const totalPayments = billingRecordsData?.length || 1;
      const paymentSuccessRate = (completedPayments / totalPayments) * 100;
      const failedAttempts = paymentAttemptsData?.filter(p => p?.attempt_status === 'failed')?.length || 0;
      
      let daysSinceLastPayment = 0;
      if (billingStatusData?.last_payment_date) {
        const lastPayment = new Date(billingStatusData?.last_payment_date);
        const today = new Date();
        daysSinceLastPayment = Math?.floor((today - lastPayment) / (1000 * 60 * 60 * 24));
      }

      setStats({
        totalBilled,
        totalPaid,
        outstandingBalance: parseFloat(billingStatusData?.outstanding_balance || 0),
        paymentSuccessRate,
        failedAttempts,
        daysSinceLastPayment
      });
    } catch (err) {
      setError(err?.message || 'Failed to load tenant billing data');
      console.error('Tenant billing data load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendTenant = async (reason, notes) => {
    try {
      const { error: updateError } = await supabase
        ?.from('tenants')
        ?.update({ status: 'suspended', updated_at: new Date()?.toISOString() })
        ?.eq('id', tenant?.id);

      if (updateError) throw updateError;

      const { error: suspensionError } = await supabase
        ?.from('tenant_suspensions')
        ?.insert({
          tenant_id: tenant?.id,
          suspended_by: user?.id,
          suspension_reason: reason,
          suspension_notes: notes,
          is_active: true
        });

      if (suspensionError) throw suspensionError;

      await loadTenantBillingData(tenant?.id);
    } catch (err) {
      setError(err?.message || 'Failed to suspend tenant');
    }
  };

  const handleReactivateTenant = async (notes) => {
    try {
      const { error: updateError } = await supabase
        ?.from('tenants')
        ?.update({ status: 'active', updated_at: new Date()?.toISOString() })
        ?.eq('id', tenant?.id);

      if (updateError) throw updateError;

      const { error: suspensionError } = await supabase
        ?.from('tenant_suspensions')
        ?.update({
          is_active: false,
          reactivated_at: new Date()?.toISOString(),
          reactivated_by: user?.id,
          reactivation_notes: notes
        })
        ?.eq('tenant_id', tenant?.id)
        ?.eq('is_active', true);

      if (suspensionError) throw suspensionError;

      await loadTenantBillingData(tenant?.id);
    } catch (err) {
      setError(err?.message || 'Failed to reactivate tenant');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading tenant billing data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
            <p className="text-foreground mb-4">Tenant not found</p>
            <Button onClick={() => navigate('/super-admin-tenant-management')}>
              Back to Tenant Management
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Page Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/super-admin-tenant-management')}
            className="mb-4"
          >
            <Icon name="ArrowLeft" size={16} />
            Back to Tenant Management
          </Button>
          
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {tenant?.logo_url ? (
                <img src={tenant?.logo_url} alt={tenant?.name} className="w-16 h-16 rounded-lg object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Building2" size={32} color="var(--color-primary)" />
                </div>
              )}
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">{tenant?.name}</h1>
                <p className="text-muted-foreground">{tenant?.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                    tenant?.status === 'active' ? 'bg-success/10 text-success border-success' :
                    tenant?.status === 'suspended'? 'bg-destructive/10 text-destructive border-destructive' : 'bg-muted text-muted-foreground border-border'
                  }`}>
                    {tenant?.status?.charAt(0)?.toUpperCase() + tenant?.status?.slice(1)}
                  </span>
                  {billingStatus?.is_overdue && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-destructive/10 text-destructive border-destructive">
                      <Icon name="AlertCircle" size={12} />
                      Payment Overdue
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button onClick={() => loadTenantBillingData(tenant?.id)} variant="outline">
              <Icon name="RefreshCw" size={16} />
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-3">
            <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
            <p className="text-destructive">{error}</p>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          <StatsCard
            title="Total Billed"
            value={`${stats?.totalBilled?.toFixed(2)}`}
            icon="FileText"
            iconColor="var(--color-primary)"
            color="primary"
            subtitle=""
            trend=""
            trendValue=""
          />
          <StatsCard
            title="Total Paid"
            value={`${stats?.totalPaid?.toFixed(2)}`}
            icon="CheckCircle"
            iconColor="var(--color-success)"
            color="success"
            subtitle=""
            trend=""
            trendValue=""
          />
          <StatsCard
            title="Outstanding"
            value={`${stats?.outstandingBalance?.toFixed(2)}`}
            icon="AlertCircle"
            iconColor={stats?.outstandingBalance > 0 ? 'var(--color-destructive)' : 'var(--color-success)'}
            color={stats?.outstandingBalance > 0 ? 'destructive' : 'success'}
            subtitle=""
            trend=""
            trendValue=""
          />
          <StatsCard
            title="Success Rate"
            value={`${stats?.paymentSuccessRate?.toFixed(1)}%`}
            icon="TrendingUp"
            iconColor={stats?.paymentSuccessRate >= 90 ? 'var(--color-success)' : 'var(--color-warning)'}
            color={stats?.paymentSuccessRate >= 90 ? 'success' : 'warning'}
            subtitle=""
            trend=""
            trendValue=""
          />
          <StatsCard
            title="Failed Attempts"
            value={stats?.failedAttempts}
            icon="XCircle"
            iconColor="var(--color-destructive)"
            color="destructive"
            subtitle=""
            trend=""
            trendValue=""
          />
          <StatsCard
            title="Days Since Payment"
            value={stats?.daysSinceLastPayment}
            icon="Calendar"
            iconColor={stats?.daysSinceLastPayment > 30 ? 'var(--color-destructive)' : 'var(--color-primary)'}
            color={stats?.daysSinceLastPayment > 30 ? 'destructive' : 'primary'}
            subtitle=""
            trend=""
            trendValue=""
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Left Column - Billing Analytics */}
          <div className="lg:col-span-2 space-y-6">
            <BillingAnalyticsPanel
              billingRecords={billingRecords}
              billingStatus={billingStatus}
            />
            <PaymentHistoryTimeline
              billingRecords={billingRecords}
              paymentAttempts={paymentAttempts}
            />
          </div>

          {/* Right Column - Controls */}
          <div className="space-y-6">
            <SuspensionManagementControls
              tenant={tenant}
              billingStatus={billingStatus}
              suspensionHistory={suspensionHistory}
              onSuspend={handleSuspendTenant}
              onReactivate={handleReactivateTenant}
            />
            <GracePeriodManager
              tenantId={tenant?.id}
              billingStatus={billingStatus}
              onUpdate={() => loadTenantBillingData(tenant?.id)}
            />
          </div>
        </div>

        {/* Communication Log */}
        <TenantCommunicationLog
          notifications={notifications}
          suspensionHistory={suspensionHistory}
        />
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default TenantBillingSuspensionDashboard;